// Темп сетевых ботов. Задача — чтобы живой игрок не вычислял бота по ритму:
// раньше боты расставляли стартовый квартал за одинаковые несколько секунд, а
// фазу снабжения заканчивали мгновенно.
//
// Модуль общий для сервера (server/index.js планирует таймеры сам) и клиента
// (OnlineBotRunner исполняет делегированную работу). Паузу между шагами бота
// обе стороны берут отсюда, но она намеренно разная: клиент-делегат —
// getOnlineBotActionDelayMs, сервер — getServerBotActionDelayMs (обычный бот —
// та же формула, в лобби ботов втрое дольше; явный — реже, чем у делегата).
// Расстановку квартала и одновременное снабжение планирует только сервер.

import {getRandomOnlineBotActionDelayMs, RANK_BOT_LOBBY_ACTION_INTERVAL_MULTIPLIER} from '../botConfig';
import {
  VISIBLE_RANKED_BOT_ACTION_DELAY_MS,
  VISIBLE_RANKED_BOT_SERVER_ACTION_DELAY_MS,
} from '../../../configs/visibleRankedBots';

// Расстановка стартового квартала: от десяти секунд до минуты — столько же
// думает человек над первым кварталом
export const BOT_SETUP_DELAY_MIN_MS = 10000;
export const BOT_SETUP_DELAY_MAX_MS = 60000;

// Дольше этой доли фазы SETUP бот тянуть не может: в быстром режиме дедлайн
// фазы 60 секунд, и иначе сервер расставил бы квартал за него
export const BOT_SETUP_DELAY_PHASE_SHARE = 0.7;

// Личный множитель пауз бота на весь матч: боты одной партии не должны
// выглядеть одинаково расторопными
export const BOT_SPEED_FACTOR_MIN = 0.8;
export const BOT_SPEED_FACTOR_MAX = 1.2;

const getRandomInRange = (min, max) => min + Math.random() * (max - min);

// Одновременная фаза снабжения: столько же думает над раздачей снабжения
// человек, а галочка «закончил» у всех на виду — мгновенная выдала бы бота
export const BOT_PARALLEL_SUPPLY_DELAY_MIN_MS = 4000;
export const BOT_PARALLEL_SUPPLY_DELAY_MAX_MS = 20000;

// Дольше этой доли общего времени фазы снабжения бот не тянет: иначе в быстром
// режиме его часть фазы за него закрывал бы таймер
export const BOT_PARALLEL_SUPPLY_DELAY_PHASE_SHARE = 0.6;

/** Множитель пауз для одного бота на весь матч */
export const getRandomBotSpeedFactor = () => getRandomInRange(BOT_SPEED_FACTOR_MIN, BOT_SPEED_FACTOR_MAX);

/**
 * Пауза бота перед расстановкой стартового квартала.
 * speedFactor — личный множитель бота, phaseDurationMs — сколько длится сама
 * фаза (у быстрого режима она короче, и потолок паузы считается от нее)
 */
export const getBotSetupDelayMs = (speedFactor = 1, phaseDurationMs = Infinity) => {
  const maxDelayMs = Math.max(
    BOT_SETUP_DELAY_MIN_MS,
    Math.round(phaseDurationMs * BOT_SETUP_DELAY_PHASE_SHARE)
  );
  const delayMs = getRandomInRange(BOT_SETUP_DELAY_MIN_MS, BOT_SETUP_DELAY_MAX_MS) * speedFactor;

  return Math.round(Math.min(delayMs, maxDelayMs));
};

/**
 * Пауза бота перед завершением своей части одновременной фазы снабжения.
 * speedFactor — личный множитель бота, phaseDurationMs — общее время фазы
 */
export const getBotParallelSupplyDelayMs = (speedFactor = 1, phaseDurationMs = Infinity) => {
  const maxDelayMs = Math.max(
    BOT_PARALLEL_SUPPLY_DELAY_MIN_MS,
    Math.round(phaseDurationMs * BOT_PARALLEL_SUPPLY_DELAY_PHASE_SHARE)
  );
  const delayMs = getRandomInRange(BOT_PARALLEL_SUPPLY_DELAY_MIN_MS, BOT_PARALLEL_SUPPLY_DELAY_MAX_MS) * speedFactor;

  return Math.round(Math.min(delayMs, maxDelayMs));
};

/**
 * Пауза перед очередным действием сетевого бота, когда его работу ведет
 * клиент-делегат (см. OnlineBotRunner): подключенный живой игрок, а если таких
 * нет — зритель с аккаунтом.
 *
 * Обычный бот прячет свой ритм: случайная пауза, помноженная на личный темп
 * (и на множитель бот-лобби, где ботам разрешено ходить медленнее — его
 * передает только сервер, см. getServerBotActionDelayMs). «Явный» бот помечен
 * ботом для всех игроков — прятаться ему не от кого, и заставлять их ждать
 * незачем: он ходит ровно (VISIBLE_RANKED_BOT_ACTION_DELAY_MS).
 */
export const getOnlineBotActionDelayMs = ({isVisibleBot = false, speedFactor = 1, lobbyMultiplier = 1} = {}) => (
  isVisibleBot
    ? VISIBLE_RANKED_BOT_ACTION_DELAY_MS
    : Math.round(getRandomOnlineBotActionDelayMs() * speedFactor * lobbyMultiplier)
);

/**
 * Пауза перед очередным действием бота, когда его ходы ведет САМ СЕРВЕР:
 * исполнителя среди подключенных участников у матча нет (см.
 * runServerBotFallbackStep на сервере).
 *
 * Обычный бот — та же случайная пауза с личным темпом, а в матче одних ботов
 * (рейтинговый витринный или обычный из потока лобби ботов) она втрое
 * дольше: смотреть его некому, спешить незачем. «Явный» бот и здесь ходит ровно,
 * но реже, чем у клиента-делегата: смотреть на него в этот момент некому
 * (VISIBLE_RANKED_BOT_SERVER_ACTION_DELAY_MS). Ни личный темп, ни множитель
 * лобби на него не действуют.
 */
export const getServerBotActionDelayMs = ({isVisibleBot = false, speedFactor = 1, isBotsOnlyMatch = false} = {}) => (
  isVisibleBot
    ? VISIBLE_RANKED_BOT_SERVER_ACTION_DELAY_MS
    : getOnlineBotActionDelayMs({
      speedFactor,
      lobbyMultiplier: isBotsOnlyMatch ? RANK_BOT_LOBBY_ACTION_INTERVAL_MULTIPLIER : 1,
    })
);
